import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

import { useTheme } from "@dfq/shared/components/ThemeProvider"

/**
 * shadcn-style `<Toaster>` wrapper around Sonner.
 *
 * Intentionally diverges from the default shadcn recipe: we consume the
 * project's own `ThemeProvider` (SH-INFRA-002) instead of `next-themes`, so the
 * toast surface tracks the `.dark` class we manage on `<html>`.
 *
 * Consumers should mount this component once at the app root, inside the
 * `ThemeProvider`, so the `theme` prop stays reactive to theme changes.
 *
 * NOTE: This file is one of only two locations allowed to import from `sonner`
 * directly — feature code must route through `@dfq/shared/hooks/useAppToast`.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { resolvedTheme } = useTheme()

  return (
    <Sonner
      theme={resolvedTheme}
      className="toaster group"
      richColors
      position="bottom-right"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
